/**
 * MathText that shrinks to fit its box (the HUD's next-problem preview, the
 * Watch layout's sum). The box takes its size from the layout, never from the
 * maths inside it.
 */
import { useMemo, useRef } from 'react';
import type { Locale, MathMarkup } from '@dropstack/core';
import { useElementSize, useFitFont } from './hooks.ts';
import { MathText } from './MathText.tsx';

export interface FittedMathProps {
  markup: MathMarkup;
  locale: Locale;
  /** Preferred and smallest font size (px). */
  basePx: number;
  minPx: number;
  /** Also fit the box's height (only when CSS gives the box a definite height). */
  fitHeight?: boolean;
  silent?: boolean;
  className?: string;
}

export function FittedMath({ markup, locale, basePx, minPx, fitHeight = false, silent = false, className }: FittedMathProps) {
  const boxRef = useRef<HTMLSpanElement>(null);
  const mathRef = useRef<HTMLSpanElement>(null);
  const box = useElementSize(boxRef);
  const px = useFitFont({
    contentRef: mathRef,
    availableWidth: box.width,
    availableHeight: fitHeight && box.height > 0 ? box.height : undefined,
    basePx,
    minPx,
    contentKey: markup,
  });
  const style = useMemo(() => ({ fontSize: px }), [px]);
  return (
    <span ref={boxRef} className={['fit-math', className].filter(Boolean).join(' ')}>
      <MathText ref={mathRef} markup={markup} locale={locale} silent={silent} style={style} />
    </span>
  );
}
